import { useCallback, useEffect, useState } from 'react';
import Card from 'components/card';
import Nodatafound from 'pages/Common/Nodatafound';
import { ThreeDots } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import { MdArrowBack } from 'react-icons/md';

import studentAssignmentServices from 'ApiService/Placement/studentAssignmentServices';
import clientServices from 'ApiService/Placement/clientServices';
import jobRoleServices from 'ApiService/Placement/jobRoleServices';
import { getCollapsedCardStatus } from '../../utils/assignmentStatus';

const FIELD_CLASS =
  'w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white';

const STATUSES = [
  'No Rounds',
  'Assigned',
  'In Progress',
  'Interview Scheduled',
  'Selected',
  'Not Selected',
  'Rejected',
];

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

/**
 * The students behind one Ongoing Interview card.
 *
 * Filters start pinned to the card (its role and its owning HR) and can be
 * cleared to widen to the rest of that company + department.
 *
 * @param {object}   card        the card that was clicked
 * @param {Array}    hrs         HRs available in the current scope, for the
 *                               "Assigned by HR" filter ([] hides it)
 * @param {Function} onBack      return to the grid
 */
const StudentListView = ({ card, hrs = [], onBack }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [roles, setRoles] = useState([]);
  const [clients, setClients] = useState([]);

  const [filters, setFilters] = useState({
    roleId: card.roleId || '',
    clientId: '',
    responseFilter: '',
    status: '',
    studentName: '',
    assignedBy: card.hrId || '',
  });

  // Roles within this card's department, so the role filter can be widened.
  useEffect(() => {
    if (!card.departmentId) return;
    (async () => {
      try {
        const response = await jobRoleServices.getRolesByDepartment(
          card.departmentId
        );
        if (response.data.success) setRoles(response.data.roles || []);
      } catch {
        // A missing role list only costs us one filter — stay quiet.
      }
    })();
  }, [card.departmentId]);

  // Clients only exist for companies flagged hasClients.
  useEffect(() => {
    if (!card.companyHasClients || !card.companyId) return;
    (async () => {
      try {
        const response = await clientServices.getClientsByCompany(
          card.companyId
        );
        if (response.data.success) setClients(response.data.clients || []);
      } catch {
        // Same — the filter simply stays empty.
      }
    })();
  }, [card.companyHasClients, card.companyId]);

  const fetchAssignments = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: 20,
          companyId: card.companyId,
          departmentId: card.departmentId,
        };
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params[key] = value;
        });

        const response = await studentAssignmentServices.getAllAssignments(
          params
        );
        if (response.data.success) {
          setAssignments(response.data.assignments || []);
          setPagination(response.data.pagination);
        } else {
          toast.error('Failed to fetch students');
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || 'Failed to fetch students'
        );
      } finally {
        setLoading(false);
      }
    },
    [card.companyId, card.departmentId, filters]
  );

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => fetchAssignments(1), 500);
    return () => clearTimeout(timer);
  }, [fetchAssignments]);

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="mt-3">
      <button
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm font-medium text-brand-500 hover:text-brand-600"
      >
        <MdArrowBack className="h-4 w-4" />
        Back to Ongoing Interviews
      </button>

      <h1 className="text-xl font-bold text-navy-700 dark:text-white">
        {card.companyName} · {card.departmentName} ·{' '}
        {card.roleName || 'JD Attachment'}
      </h1>
      {card.hrName && (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          by: {card.hrName} · started {formatDate(card.startDate)}
        </p>
      )}

      <Card extra="mb-6 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-white">
              Job Role
            </label>
            <select
              value={filters.roleId}
              onChange={(e) => handleFilterChange('roleId', e.target.value)}
              className={FIELD_CLASS}
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.roleName}
                </option>
              ))}
            </select>
          </div>

          {card.companyHasClients && (
            <div>
              <label className="mb-1 block text-sm text-gray-600 dark:text-white">
                Client
              </label>
              <select
                value={filters.clientId}
                onChange={(e) => handleFilterChange('clientId', e.target.value)}
                className={FIELD_CLASS}
              >
                <option value="">All Clients</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.clientName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-white">
              Show
            </label>
            <select
              value={filters.responseFilter}
              onChange={(e) =>
                handleFilterChange('responseFilter', e.target.value)
              }
              className={FIELD_CLASS}
            >
              {/* Mirrors the card's rows; "All Students" equals its total. */}
              <option value="">All Students</option>
              <option value="accepted">Accepted</option>
              <option value="declined">Declined</option>
              <option value="auto-declined">Auto Declined</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-white">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={FIELD_CLASS}
            >
              <option value="">All Statuses</option>
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-white">
              Search Student
            </label>
            <input
              type="text"
              value={filters.studentName}
              onChange={(e) =>
                handleFilterChange('studentName', e.target.value)
              }
              placeholder="Name, email or phone"
              className={FIELD_CLASS}
            />
          </div>

          {hrs.length > 0 && (
            <div>
              <label className="mb-1 block text-sm text-gray-600 dark:text-white">
                Assigned by HR
              </label>
              <select
                value={filters.assignedBy}
                onChange={(e) =>
                  handleFilterChange('assignedBy', e.target.value)
                }
                className={FIELD_CLASS}
              >
                <option value="">All HRs</option>
                {hrs.map((hr) => (
                  <option key={hr._id} value={hr._id}>
                    {hr.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <ThreeDots color="#4318FF" height={60} width={60} />
        </div>
      ) : assignments.length === 0 ? (
        <Nodatafound title="No students match these filters" />
      ) : (
        <>
          <div className="space-y-3">
            {assignments.map((assignment) => {
              const status = getCollapsedCardStatus(assignment);
              return (
                <Card key={assignment._id} extra="w-full p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-semibold text-navy-700 dark:text-white">
                        {assignment.studentId?.name || '-'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {assignment.studentId?.email || ''}
                        {assignment.studentId?.contactNumber
                          ? ` · ${assignment.studentId.contactNumber}`
                          : ''}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          Role:{' '}
                          {assignment.roleId?.roleName ||
                            assignment.jobTitle ||
                            assignment.jdAttachment?.fileName ||
                            '-'}
                        </span>
                        {assignment.clientId?.clientName && (
                          <span>Client: {assignment.clientId.clientName}</span>
                        )}
                        <span>
                          Assigned: {formatDate(assignment.assignedDate)}
                        </span>
                        <span>By: {assignment.assignedBy?.name || '-'}</span>
                      </div>
                      {/* Why the student dropped out — set by the student when
                          they refuse, or by the expiry sweep when they time out. */}
                      {assignment.revertReason && (
                        <p className="mt-2 rounded-lg bg-red-50 px-2 py-1 text-xs text-red-700 dark:bg-navy-900 dark:text-red-300">
                          Reason: {assignment.revertReason}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${status.bg}`}
                      >
                        {status.label}
                      </span>
                      {assignment.scheduledInterviews?.length > 0 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {assignment.scheduledInterviews.length} round
                          {assignment.scheduledInterviews.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {pagination.pages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchAssignments(pagination.page - 1)}
                className="rounded-xl border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-40 dark:border-white/10 dark:text-white"
              >
                Prev
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Page {pagination.page} of {pagination.pages} ({pagination.total}{' '}
                students)
              </span>
              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => fetchAssignments(pagination.page + 1)}
                className="rounded-xl border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-40 dark:border-white/10 dark:text-white"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StudentListView;
